import React, { useEffect, useRef, useState } from "react";
import { Share2, Check, Loader, Pause, Play, Volume2, VolumeX } from "lucide-react";

/**
 * Welcome page intro reel — uses the brand-owned MP4 served from
 * /resmo-intro.mp4 (1280×720, ~12MB, H.264 + faststart).
 *
 * Controls:
 *  - Play / Pause (large primary action; mirrors native <video> state)
 *  - Mute / Unmute (autoplay starts MUTED for browser policy compliance —
 *    the unmute button is what triggers audio)
 *  - Share Resmo (copy/share the /welcome URL)
 */
const VIDEO_SRC = "/resmo-intro.mp4?v=2";
const POSTER_SRC = "/resmo-intro-poster.jpg";

export default function WelcomeIntro() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [loading, setLoading] = useState(true);
  const [shareState, setShareState] = useState("idle"); // idle | copied

  // Sync state with native <video> events so the UI stays accurate even when
  // the user uses the right-click native context menu or keyboard shortcuts.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onWaiting = () => setLoading(true);
    const onCanPlay = () => setLoading(false);
    const onVolumeChange = () => setMuted(v.muted);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("volumechange", onVolumeChange);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("volumechange", onVolumeChange);
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => { /* swallow autoplay rejection */ });
    else v.pause();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    // Unmute → seek to start so the user hears the intro from the top
    if (v.muted) v.currentTime = 0;
    v.muted = !v.muted;
    if (v.paused) v.play().catch(() => { /* ignore */ });
  };

  const handleVideoClick = () => {
    // Click on the stage = unmute + restart (same as the unmute button)
    if (muted) toggleMute();
    else togglePlay();
  };

  const handleShare = async () => {
    try {
      const url = `${window.location.origin}/welcome`;
      const shareData = {
        title: "Resmo — career intelligence companion",
        text: "Meet Resmo — he tailors your resume to any job in 30 seconds.",
        url,
      };
      if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareState("copied");
      setTimeout(() => setShareState("idle"), 1800);
    } catch (_e) {
      setShareState("idle");
    }
  };

  return (
    <div className="w-full h-full flex flex-col" data-testid="welcome-intro">
      {/* Video stage — fills available column height (h-full) instead of
          enforcing 16:9. The <video> element inside uses object-fit:cover so
          it scales + crops cleanly to whatever shape the column dictates,
          letting this card match the Quick-Start picker's height exactly. */}
      <div
        className="rm-card-static overflow-hidden relative w-full cursor-pointer flex-1"
        style={{
          background: "radial-gradient(circle at 50% 30%, #1E3A5F 0%, #0F1B2D 75%)",
          borderColor: "#0F1B2D",
          boxShadow: "6px 6px 0 0 #FF6A00",
          padding: 0,
          width: "100%",
          minHeight: "320px",
        }}
        onClick={handleVideoClick}
        data-testid="welcome-intro-stage"
      >
        {loading && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none"
            style={{ zIndex: 2, color: "#FFC145" }}
            aria-hidden
          >
            <Loader size={22} className="animate-spin" />
            <div className="font-mono-rm text-[11px] uppercase tracking-widest">
              Loading…
            </div>
          </div>
        )}

        <video
          ref={videoRef}
          src={VIDEO_SRC}
          poster={POSTER_SRC}
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: "cover", background: "#0F1B2D", zIndex: 1 }}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="welcome-intro-video"
        >
          Sorry, your browser doesn't support the video tag.
        </video>

        {/* Muted-state overlay nudge — only when muted and playing */}
        {muted && playing && !loading && (
          <div
            className="absolute bottom-3 left-3 inline-flex items-center gap-1 font-mono-rm uppercase text-[10px] tracking-widest px-2 py-1 rounded pointer-events-none"
            style={{
              background: "rgba(15, 27, 45, 0.85)",
              color: "#FFC145",
              border: "1px solid #FFC145",
              fontWeight: 700,
              zIndex: 3,
            }}
            data-testid="welcome-intro-muted-hint"
          >
            <VolumeX size={11} /> · Tap to hear Resmo
          </div>
        )}
      </div>

      {/* Controls row — Play/Pause + Mute/Unmute + Share */}
      <div className="mt-4 flex items-center justify-center gap-3 flex-wrap"
           data-testid="welcome-intro-controls">
        <button
          type="button"
          onClick={togglePlay}
          className="rm-btn text-[11px] px-3 py-1.5"
          style={{
            background: playing ? "#B91C1C" : "#047857",
            color: "white",
            border: "2px solid #0F1B2D",
            boxShadow: "3px 3px 0 0 #0F1B2D",
            fontWeight: 700,
          }}
          data-testid={playing ? "welcome-intro-stop-btn" : "welcome-intro-play-btn"}
          title={playing ? "Pause the intro" : "Play the intro"}
        >
          {playing
            ? <><Pause size={11} fill="white" /> Pause</>
            : <><Play size={11} fill="white" /> Play</>}
        </button>

        <button
          type="button"
          onClick={toggleMute}
          className="rm-btn text-[11px] px-3 py-1.5"
          style={{
            background: muted ? "#0F1B2D" : "#FF6A00",
            color: muted ? "#FFC145" : "white",
            border: "2px solid #0F1B2D",
            boxShadow: "3px 3px 0 0 #FF6A00",
            fontWeight: 700,
          }}
          data-testid={muted ? "welcome-intro-unmute-btn" : "welcome-intro-mute-btn"}
          title={muted ? "Hear Resmo" : "Mute Resmo"}
        >
          {muted
            ? <><Volume2 size={11} /> Unmute</>
            : <><VolumeX size={11} /> Mute</>}
        </button>

        {/* Share Resmo pill */}
        <button
          type="button"
          onClick={handleShare}
          className="rm-btn text-[11px] px-3 py-1.5"
          style={{
            background: shareState === "copied" ? "#047857" : "#0F1B2D",
            color: shareState === "copied" ? "white" : "#FFC145",
            border: "2px solid #0F1B2D",
            boxShadow: "3px 3px 0 0 #FF6A00",
            fontWeight: 700,
          }}
          data-testid="intro-share-btn"
          title="Copy a sharable Welcome link"
        >
          {shareState === "copied" ? <Check size={11} /> : <Share2 size={11} />}
          {shareState === "copied" ? "Link copied!" : "Share Resmo"}
        </button>
      </div>
    </div>
  );
}
